import { CommitmentTabProps } from "features/commitments/view/tabs/CommitmentTabProps";
import {
  useHypotheticalWaterfallPerformanceFilterDataRetrieveSuspense,
  useHypotheticalWaterfallPerformanceStatsByPeriodRetrieveSuspense,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundFamilyListSuspense } from "gen/endpoints/fund-family/fund-family";
import { compact } from "lodash-es";
import React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { FundFamilySelectButton } from "shared/components/input/select/FundFamilySelectButton";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { NonNullStringArrayParam } from "shared/utils/QueryParamUtils";
import { useQueryParam } from "use-query-params";

export const HypotheticalPerformanceTab: React.FC = () => {
  const { investorId, investorEntityId, commitmentId, fundFamilyId } =
    useOutletContext<CommitmentTabProps>();

  const { data: fundFamilies } = useFundFamilyListSuspense({
    investor_id: investorId,
    investor_entity_id: investorEntityId,
  });

  const { data: fundFamilyFilterData } =
    useHypotheticalWaterfallPerformanceFilterDataRetrieveSuspense({
      fund_family_ids: fundFamilyId
        ? [fundFamilyId]
        : fundFamilies.map((fundFamily) => String(fundFamily.id)),
    });
  const { t } = useTranslation(["financialReporting"]);
  const fundFamilyIdToIsEnabled =
    fundFamilyFilterData.fund_family_filter_options?.reduce(
      (accumulator, currentValue) => {
        accumulator[currentValue.id] = !currentValue.is_disabled;
        return accumulator;
      },
      {} as Record<string, boolean>,
    ) || ({} as Record<string, boolean>);
  const selectableFundFamilyIds = fundFamilyId
    ? [fundFamilyId]
    : compact(
        fundFamilies.map((fundFamily) =>
          fundFamilyIdToIsEnabled[fundFamily.id!] ? fundFamily.id : false,
        ),
      );
  const [fundFamilyIds, setFundFamilyIds] = useQueryParam(
    "fundFamilyId",
    NonNullStringArrayParam(selectableFundFamilyIds),
  );
  const { data: performanceTable, isLoading } =
    useHypotheticalWaterfallPerformanceStatsByPeriodRetrieveSuspense(
      {
        investor_id: investorId,
        investor_entity_id: investorEntityId,
        security_ownership_id: commitmentId,
        fund_family_ids: fundFamilyIds,
      },
      {
        request: {
          timeout: 20_000,
        },
      },
    );
  const emptyTitle = commitmentId
    ? t("commitment:tabs.performance.table.empty.title")
    : investorEntityId
      ? t("investorEntity:tabs.performance.table.empty.title")
      : t("investor:tabs.performance.table.empty.title");
  const emptyDescription = commitmentId
    ? t("commitment:tabs.performance.table.empty.description")
    : investorEntityId
      ? t("investorEntity:tabs.performance.table.empty.description")
      : t("investor:tabs.performance.table.empty.description");

  return (
    <PageTabBody>
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>
            {t("financialReporting:performanceTab.blendedQuarter.title")}
          </TableBoxTitle>
          <DataTableBoxActions>
            {!commitmentId &&
              fundFamilyFilterData.fund_family_filter_options && (
                <FundFamilySelectButton
                  fundFamilyFilterOptions={
                    fundFamilyFilterData.fund_family_filter_options
                  }
                  selectedFundFamilyIds={fundFamilyIds}
                  onSelectedFundFamilyIdsChange={setFundFamilyIds}
                />
              )}
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody mb="0">
          <DataTable
            data={performanceTable}
            isLoading={isLoading}
            emptyStateProps={{
              title: emptyTitle,
              description: emptyDescription,
            }}
          />
        </TableBoxBody>
      </TableBox>
    </PageTabBody>
  );
};
